import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';

const VisionSection = () => {
  const { t } = useLanguage();

  const pillars = [
    {
      number: '01',
      title: t('vision.pillar1Title'),
      description: t('vision.pillar1Desc'),
    },
    {
      number: '02',
      title: t('vision.pillar2Title'),
      description: t('vision.pillar2Desc'),
    },
    {
      number: '03',
      title: t('vision.pillar3Title'),
      description: t('vision.pillar3Desc'),
    },
  ];

  return (
    <section 
      id="approach"
      data-testid="vision-section"
      className="py-16 md:py-24 bg-[#1A3A4A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-6">
              {t('vision.title')}
            </h2>
            <p className="font-body text-lg text-white/70 mb-10 leading-relaxed">
              {t('vision.subtitle')}
            </p>

            {/* Pillars as stacked items */}
            <div className="space-y-6">
              {pillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                  className="flex gap-5"
                >
                  {/* Number indicator */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-md bg-[#60B5AF]/20 flex items-center justify-center">
                    <span className="font-heading text-sm font-bold text-[#60B5AF]">{pillar.number}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">
                      {pillar.title}
                    </h3>
                    <p className="font-body text-white/60 text-sm leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Visual Element / Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Abstract geometric visual */}
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-lg p-8 md:p-12 border border-white/10">
              {/* Large stat display */}
              <div className="text-center mb-8">
                <span className="font-heading text-6xl md:text-7xl lg:text-8xl font-bold text-[#60B5AF]">3</span>
                <p className="font-body text-white/70 text-lg mt-2">Core Pillars</p>
              </div>

              {/* Decorative elements */}
              <div className="grid grid-cols-3 gap-4">
                <div className="h-2 bg-[#60B5AF]/60 rounded-full" />
                <div className="h-2 bg-[#60B5AF]/40 rounded-full" />
                <div className="h-2 bg-[#60B5AF]/20 rounded-full" />
              </div>

              {/* Bottom text */}
              <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-heading text-2xl font-bold text-white">Clarity</p>
                  <p className="font-body text-xs text-white/50 mt-1">Decisions</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-white">Value</p>
                  <p className="font-body text-xs text-white/50 mt-1">Creation</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-white">Visibility</p>
                  <p className="font-body text-xs text-white/50 mt-1">Performance</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;
